/**
 * The assistants a user can connect to Foundry Connect, and what each one needs
 * typed to get there.
 *
 * Status is a build time constant on purpose. The production nginx templates
 * ship inside the frontend image, so the page and the edge that serves it always
 * move together; a runtime probe would spend a request per page view on a
 * question that only changes at deploy. See spec section 9.
 */

/** Matches the server name the published plugin registers in its .mcp.json. */
export const MCP_CONNECTOR_NAME = "foundry";

export type ConnectClientId = "claude" | "copilot" | "codex" | "chatgpt";

export type ConnectClientStatus = "available" | "unavailable";

export interface ConnectClientStep {
  /** Prose shown above the command, or on its own when there is no command. */
  readonly description: string;
  /** Built from the connector URL. Absent when the step is prose only. */
  readonly command?: (mcpUrl: string) => string;
}

export interface ConnectClient {
  readonly id: ConnectClientId;
  readonly label: string;
  readonly status: ConnectClientStatus;
  /** One line under the heading, describing what connecting will do. */
  readonly summary: string;
  readonly steps: readonly ConnectClientStep[];
  /** Shown in place of steps when the status is "unavailable". */
  readonly unavailableReason?: string;
}

export const CONNECT_CLIENTS: readonly ConnectClient[] = [
  {
    id: "claude",
    label: "Claude",
    status: "available",
    summary:
      "Add Foundry Connect to claude.ai, Claude Desktop or Claude Code as a custom connector. A browser window opens to sign you in, so you never copy a credential.",
    steps: [
      {
        description:
          "In claude.ai or Claude Desktop, add a custom connector using the URL above. For Claude Code, run:",
        command: mcpUrl =>
          `claude mcp add --transport http ${MCP_CONNECTOR_NAME} ${mcpUrl}`,
      },
    ],
  },
  {
    id: "copilot",
    label: "Copilot CLI",
    status: "available",
    summary:
      "Copilot follows the sign in challenge the server sends, so it needs the URL and nothing else.",
    steps: [
      {
        description: "Register the server:",
        command: mcpUrl =>
          `copilot mcp add --transport http ${MCP_CONNECTOR_NAME} ${mcpUrl}`,
      },
      {
        description:
          "Then start Copilot and approve the sign in prompt. Copilot CLI has no login subcommand, so signing in happens inside a session rather than from the shell.",
      },
    ],
  },
  {
    id: "codex",
    label: "Codex CLI",
    status: "available",
    summary:
      "Codex needs the server registered by hand, once per machine, and then signs in through the browser.",
    steps: [
      {
        description: "Register the server:",
        command: mcpUrl =>
          `codex mcp add ${MCP_CONNECTOR_NAME} --url ${mcpUrl}`,
      },
      {
        description: "Then sign in:",
        command: () => `codex mcp login ${MCP_CONNECTOR_NAME}`,
      },
      {
        description:
          "If browser sign in does not work, a personal access credential can be used instead. Open the Personal Access Tokens tab, generate an MCP credential, and use the Codex snippet shown there.",
      },
    ],
  },
  {
    id: "chatgpt",
    label: "ChatGPT",
    status: "unavailable",
    summary:
      "There is no self serve way to point ChatGPT at this instance yet.",
    unavailableReason:
      "ChatGPT reads no local configuration file, so there is nowhere to type this instance's address. The routes that do exist, the public directory and a registered connection, both need an identifier provisioned by OpenAI for this specific host. Until that changes, use Codex CLI for an OpenAI tool, or Claude.",
    steps: [],
  },
];

const KNOWN_IDS = new Set<string>(CONNECT_CLIENTS.map(client => client.id));

export const DEFAULT_CONNECT_CLIENT_ID: ConnectClientId = "claude";

/**
 * Maps a `?client=` value onto a client. Anything unrecognised falls back to the
 * default rather than erroring, so a stale documentation link degrades to a
 * working page instead of a blank one.
 */
export const resolveClientId = (raw: string | null): ConnectClientId =>
  raw && KNOWN_IDS.has(raw)
    ? (raw as ConnectClientId)
    : DEFAULT_CONNECT_CLIENT_ID;
